import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Calendar, ArrowLeft, X, Edit2, Trash2, Send, AlertCircle, Check, Coffee, FileSpreadsheet } from "lucide-react";
import ConfirmModal from "../components/ConfirmModal";
import { useAuth } from "../AuthContext";
import { useNotifications } from "../NotificationContext";
import API_URL from '../config';
import * as XLSX from 'xlsx';

const monthNames = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const dayNamesShort = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

const employeeColors = [
  { bg: "bg-indigo-100", text: "text-indigo-700", border: "border-indigo-200" },
  { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-200" },
  { bg: "bg-pink-100", text: "text-pink-700", border: "border-pink-200" },
  { bg: "bg-rose-100", text: "text-rose-700", border: "border-rose-200" },
  { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200" },
  { bg: "bg-amber-100", text: "text-amber-700", border: "border-amber-200" },
  { bg: "bg-emerald-100", text: "text-emerald-700", border: "border-emerald-200" },
  { bg: "bg-teal-100", text: "text-teal-700", border: "border-teal-200" },
  { bg: "bg-cyan-100", text: "text-cyan-700", border: "border-cyan-200" },
  { bg: "bg-sky-100", text: "text-sky-700", border: "border-sky-200" },
];

const getMonthBounds = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  return { firstDay, lastDay };
};

const getDaysInMonth = (date) => {
  const { firstDay, lastDay } = getMonthBounds(date);
  const days = [];

  // Start 3 days before the first day of the month
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - 3);

  // End 3 days after the last day of the month
  const endDate = new Date(lastDay);
  endDate.setDate(endDate.getDate() + 3);

  for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
    days.push(new Date(d));
  }

  return days;
};

const isSameDay = (d1, d2) =>
  d1.getFullYear() === d2.getFullYear() &&
  d1.getMonth() === d2.getMonth() &&
  d1.getDate() === d2.getDate();

const formatDateLocal = (d) => {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatTime = (datetime) => {
  return new Date(datetime).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

export default function VueMensuelle() {
  const navigate = useNavigate();
  const { isTeamLeaderOrAbove, isManager } = useAuth();
  const { refreshTrigger } = useNotifications();

  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [approvedLeaves, setApprovedLeaves] = useState([]);
  const [leaveToDelete, setLeaveToDelete] = useState(null); // leave pending deletion (confirm modal)
  const [deletingLeave, setDeletingLeave] = useState(false);
  const [dayOffs, setDayOffs] = useState([]); // day_off events from /calendar/week
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState("table");

  // Shift action modal state
  const [shiftActionModal, setShiftActionModal] = useState({
    show: false,
    shift: null,
    mode: null, // "edit", "request", "view-request"
    request: null,
  });

  const [editShift, setEditShift] = useState({
    user_id: "",
    start_time: "",
    end_time: "",
    no_show: false,
    room_id: "",
    break_duration: 0,
    comment: "",
  });

  const [tags, setTags] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  // Request modification state (for employees)
  const [requestModification, setRequestModification] = useState({
    start_time: "",
    end_time: "",
  });

  // User ordering from localStorage (same as GestionShifts)
  const [usersOrder] = useState(() => {
    const saved = localStorage.getItem('usersOrder');
    return saved ? JSON.parse(saved) : [];
  });

  // Tooltip state
  const [hoveredUserId, setHoveredUserId] = useState(null);

  // Export modal state
  const [exportModal, setExportModal] = useState({ show: false });
  const [exportLoading, setExportLoading] = useState(false);
  const [exportDateRange, setExportDateRange] = useState({ start: '', end: '' });
  const [exportSelectedUsers, setExportSelectedUsers] = useState([]);

  const token = localStorage.getItem("token");
  const SHIFTS_API = `${API_URL}/shifts`;
  const USERS_API = `${API_URL}/users`;
  const LEAVES_API = `${API_URL}/api/leaves`;
  const REQUESTS_API = `${API_URL}/requests`;
  const TAGS_API = `${API_URL}/tags`;
  const ROOMS_API = `${API_URL}/rooms`;

  useEffect(() => {
    fetchMonthlyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth, refreshTrigger]);

  // Detect screen size
  useEffect(() => {
    const handleResize = () => {
      setViewMode(window.innerWidth < 768 ? "cards" : "table");
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fetchMonthlyData = async () => {
    setLoading(true);
    await Promise.all([
      fetchMonthlyShifts(),
      fetchUsers(),
      fetchApprovedLeaves(),
      fetchRequests(),
      fetchTags(),
      fetchRooms(),
      fetchCalendarDayOffs()
    ]);
    setLoading(false);
  };

  const fetchMonthlyShifts = async () => {
    try {
      const { firstDay, lastDay } = getMonthBounds(currentMonth);

      // Extend range by 3 days before and after
      const startDate = new Date(firstDay);
      startDate.setDate(startDate.getDate() - 3);
      const endDate = new Date(lastDay);
      endDate.setDate(endDate.getDate() + 3);

      const startStr = formatDateLocal(startDate);
      const endStr = formatDateLocal(endDate);

      const res = await fetch(
        `${SHIFTS_API}/?start_date=${startStr}&end_date=${endStr}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.ok) {
        const data = await res.json();
        setShifts(data);

        // Extract users from shifts
        if (data.length > 0) {
          setUsers(prevUsers => {
            const userMap = new Map();
            prevUsers.forEach(u => userMap.set(u.id, u));

            data.forEach(shift => {
              if (shift.user_id && !userMap.has(shift.user_id)) {
                userMap.set(shift.user_id, {
                  id: shift.user_id,
                  email: shift.user_email || `User ${shift.user_id}`,
                  full_name: shift.user_name || null,
                });
              }
            });

            return Array.from(userMap.values());
          });
        }
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des shifts:", err);
    }
  };

  const fetchUsers = async () => {
    // Ne pas faire de requête si non-manager (évite 403)
    if (!isTeamLeaderOrAbove) {
      setUsers([]); // Liste vide - pas besoin des noms pour VueMensuelle
      return;
    }

    try {
      const res = await fetch(`${USERS_API}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      } else {
        console.error("Erreur lors de la récupération des utilisateurs");
        setUsers([]);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des utilisateurs:", err);
      setUsers([]);
    }
  };

  const fetchApprovedLeaves = async () => {
    try {
      const endpoint = isTeamLeaderOrAbove
        ? `${LEAVES_API}/team-requests?status_filter=approved`
        : `${LEAVES_API}/my-requests`;

      const res = await fetch(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        const approved = isTeamLeaderOrAbove
          ? data.filter(leave => leave.status === 'approved')
          : data.filter(leave => leave.status === 'approved');
        setApprovedLeaves(approved);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des congés:", err);
    }
  };

  // Opens the styled confirmation modal for a leave deletion (manager only)
  const requestDeleteLeave = (leave) => {
    if (!isManager || !leave?.id) return;
    setLeaveToDelete(leave);
  };

  const confirmDeleteLeave = async () => {
    const leave = leaveToDelete;
    if (!isManager || !leave?.id) return;

    setDeletingLeave(true);
    try {
      const res = await fetch(`${LEAVES_API}/${leave.id}/cancel`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok && res.status !== 204) {
        let detail = "Erreur lors de la suppression du congé";
        try { detail = (await res.json()).detail || detail; } catch (_) {}
        throw new Error(detail);
      }

      setLeaveToDelete(null);
      await fetchApprovedLeaves();
    } catch (err) {
      console.error(err);
      alert(err.message);
    } finally {
      setDeletingLeave(false);
    }
  };

  const fetchRequests = async () => {
    try {
      const res = await fetch(`${REQUESTS_API}/?status=pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        const data = await res.json();
        setRequests(data);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des demandes:", err);
    }
  };

  const fetchTags = async () => {
    try {
      const res = await fetch(`${TAGS_API}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTags(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRooms = async () => {
    try {
      const res = await fetch(`${ROOMS_API}/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setRooms(data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  /**
   * Fetch day-offs from GET /calendar/week for every Monday of the current month.
   * The endpoint returns events with type=="day_off" containing { user_id, date }.
   */
  const fetchCalendarDayOffs = async () => {
    try {
      const { firstDay, lastDay } = getMonthBounds(currentMonth);

      // Collect all Mondays we need to cover (one per week spanning the month)
      const mondays = [];
      const cursor = new Date(firstDay);
      // Go back to the Monday of the first week
      const dayOfWeek = cursor.getDay(); // 0=Sun … 6=Sat
      const daysToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      cursor.setDate(cursor.getDate() + daysToMonday);

      while (cursor <= lastDay) {
        mondays.push(formatDateLocal(new Date(cursor)));
        cursor.setDate(cursor.getDate() + 7);
      }

      // Fetch each week in parallel
      const results = await Promise.all(
        mondays.map(monday =>
          fetch(`${API_URL}/calendar/week?week_start=${monday}`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null)
        )
      );

      // Flatten and keep only day_off events
      const collected = [];
      results.forEach(resp => {
        if (!resp?.events) return;
        resp.events.forEach(ev => {
          if (ev.type === 'day_off') collected.push(ev);
        });
      });

      setDayOffs(collected);
    } catch (err) {
      console.error('Erreur lors de la récupération des jours de repos:', err);
    }
  };

  // Get pending request for a shift
  const getPendingRequestForShift = (shiftId) => {
    return requests.find(
      (req) => req.shift_id === shiftId && req.status === "pending"
    );
  };

  // Handle shift click - open modal
  const handleShiftClick = (shift) => {
    if (!shift?.start_time || !shift?.end_time) {
      console.error("Shift missing time data:", shift);
      return;
    }

    const shiftUser = users.find(u => u.id === shift.user_id);
    const currentUserId = parseInt(localStorage.getItem("user_id"));
    const isOwnShift = shift.user_id === currentUserId;

    if (isTeamLeaderOrAbove) {
      // Manager can edit or delete
      setEditShift({
        user_id: shift.user_id,
        start_datetime: shift.start_time?.slice(0, 16) || "",
        end_datetime: shift.end_time?.slice(0, 16) || "",
        start_time: shift.start_time.slice(11, 16),
        end_time: shift.end_time.slice(11, 16),
        no_show: shift.no_show || false,
        room_id: shift.room_id || "",
        break_duration: parseInt(shift.break_duration) || 0,
        comment: shift.comment || "",
      });
      setSelectedTagIds(shift.tags ? shift.tags.map(t => t.id) : []);
      setShiftActionModal({
        show: true,
        shift: { ...shift, user: shiftUser, user_name: shiftUser?.full_name, user_email: shiftUser?.email },
        mode: "edit",
      });
    } else {
      // Non-manager can only interact with their own shifts
      if (!isOwnShift) return;

      const pendingRequest = getPendingRequestForShift(shift.id);

      if (pendingRequest) {
        // Show request details
        setShiftActionModal({
          show: true,
          shift: { ...shift, user: shiftUser, user_name: shiftUser?.full_name, user_email: shiftUser?.email },
          mode: "view-request",
          request: pendingRequest,
        });
      } else {
        // Allow request modification
        setRequestModification({
          start_time: shift.start_time.slice(11, 16),
          end_time: shift.end_time.slice(11, 16),
        });
        setShiftActionModal({
          show: true,
          shift: { ...shift, user: shiftUser, user_name: shiftUser?.full_name, user_email: shiftUser?.email },
          mode: "request",
        });
      }
    }
  };

  // Manager: Update shift
  const handleUpdateShift = async () => {
    setLoading(true);
    try {
      const updatedShift = {
        start_time: editShift.start_datetime?.length === 16 ? `${editShift.start_datetime}:00` : editShift.start_datetime,
        end_time: editShift.end_datetime?.length === 16 ? `${editShift.end_datetime}:00` : editShift.end_datetime,
        user_id: editShift.user_id || shiftActionModal.shift.user_id,
        room_id: editShift.room_id ? parseInt(editShift.room_id) : null,
        no_show: editShift.no_show,
        break_duration: parseInt(editShift.break_duration) || 0,
        comment: editShift.comment || "",
        tag_ids: selectedTagIds,
      };

      const res = await fetch(`${SHIFTS_API}/${shiftActionModal.shift.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedShift),
      });

      if (res.ok) {
        await fetchMonthlyData();
        setShiftActionModal({ show: false, shift: null, mode: null });
      } else {
        const error = await res.json();
        alert(`Erreur: ${error.detail || 'Impossible de modifier le shift'}`);
      }
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la modification du shift");
    } finally {
      setLoading(false);
    }
  };

  // Manager: Delete shift
  const handleDeleteShift = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce shift ?")) return;

    setLoading(true);
    try {
      const res = await fetch(`${SHIFTS_API}/${shiftActionModal.shift.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        await fetchMonthlyData();
        setShiftActionModal({ show: false, shift: null, mode: null });
      } else {
        const error = await res.json();
        alert(`Erreur: ${error.detail || 'Impossible de supprimer le shift'}`);
      }
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la suppression du shift");
    } finally {
      setLoading(false);
    }
  };

  // Employee: Request modification
  const handleRequestModification = async () => {
    setLoading(true);
    try {
      const shiftDate = formatDateLocal(new Date(shiftActionModal.shift.start_time));
      const requestData = {
        shift_id: shiftActionModal.shift.id,
        start_time: `${shiftDate}T${requestModification.start_time}:00`,
        end_time: `${shiftDate}T${requestModification.end_time}:00`,
      };

      const res = await fetch(`${REQUESTS_API}/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (res.ok) {
        await fetchMonthlyData();
        setShiftActionModal({ show: false, shift: null, mode: null });
      } else {
        const error = await res.json();
        alert(`Erreur: ${error.detail || 'Impossible de créer la demande'}`);
      }
    } catch (err) {
      console.error(err);
      alert("Erreur lors de la création de la demande");
    } finally {
      setLoading(false);
    }
  };

  // Manager: Approve request
  const handleApproveRequest = async () => {
    const pendingRequest = getPendingRequestForShift(shiftActionModal.shift.id);
    if (!pendingRequest) return;

    setLoading(true);
    try {
      const res = await fetch(`${REQUESTS_API}/${pendingRequest.id}/approve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        await fetchMonthlyData();
        setShiftActionModal({ show: false, shift: null, mode: null });
      } else {
        const error = await res.json();
        alert(`Erreur: ${error.detail || 'Impossible d\'approuver la demande'}`);
      }
    } catch (err) {
      console.error(err);
      alert("Erreur lors de l'approbation de la demande");
    } finally {
      setLoading(false);
    }
  };

  // Manager: Reject request
  // Handle modifying a shift from a request
  const handleModifyFromRequest = async () => {
    const pendingRequest = getPendingRequestForShift(shiftActionModal.shift.id);
    if (!pendingRequest) return;

    setLoading(true);
    try {
      const res = await fetch(`${REQUESTS_API}/${pendingRequest.id}/reject`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        await fetchMonthlyData();
        // Le modal restera ouvert mais pendingRequest sera null, montrant ainsi les champs de modification
      } else {
        const error = await res.json();
        alert(`Erreur: ${error.detail || 'Impossible de rejeter la demande'}`);
      }
    } catch (err) {
      console.error(err);
      alert("Erreur lors du rejet de la demande");
    } finally {
      setLoading(false);
    }
  };

  const handleRejectRequest = async () => {
    const pendingRequest = getPendingRequestForShift(shiftActionModal.shift.id);
    if (!pendingRequest) return;

    setLoading(true);
    try {
      const res = await fetch(`${REQUESTS_API}/${pendingRequest.id}/reject`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.ok) {
        await fetchMonthlyData();
        setShiftActionModal({ show: false, shift: null, mode: null });
      } else {
        const error = await res.json();
        alert(`Erreur: ${error.detail || 'Impossible de rejeter la demande'}`);
      }
    } catch (err) {
      console.error(err);
      alert("Erreur lors du rejet de la demande");
    } finally {
      setLoading(false);
    }
  };

  // Open export modal — pre-fill dates with the current month
  const openExportModal = () => {
    const { firstDay, lastDay } = getMonthBounds(currentMonth);
    setExportDateRange({
      start: formatDateLocal(firstDay),
      end: formatDateLocal(lastDay),
    });
    setExportSelectedUsers(sortedUsers.map(u => u.id));
    setExportModal({ show: true });
  };

  // Toggle one user in the export selection
  const toggleExportUser = (userId) => {
    setExportSelectedUsers(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  // Build and download the Excel file
  const handleExportShifts = async () => {
    if (!exportDateRange.start || !exportDateRange.end) {
      alert('Veuillez sélectionner une période.');
      return;
    }
    if (exportSelectedUsers.length === 0) {
      alert('Veuillez sélectionner au moins un employé.');
      return;
    }

    setExportLoading(true);
    try {
      // Fetch shifts for the selected date range from the API
      const res = await fetch(
        `${SHIFTS_API}/?start_date=${exportDateRange.start}&end_date=${exportDateRange.end}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let rangeShifts = [];
      if (res.ok) {
        const data = await res.json();
        // Filter by selected users
        rangeShifts = data.filter(s => exportSelectedUsers.includes(s.user_id));
      }

      const wb = XLSX.utils.book_new();
      const colWidthsAll = [{ wch: 22 }, { wch: 26 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 22 }, { wch: 10 }, { wch: 10 }];

      // --- Sheet 1: All shifts (flat table) ---
      const allRows = rangeShifts
        .slice()
        .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
        .map(shift => {
          const start = new Date(shift.start_time);
          const end = new Date(shift.end_time);
          const durationH = ((end - start) / (1000 * 60 * 60)).toFixed(2);
          const employee = users.find(u => u.id === shift.user_id);
          return {
            "Employé": employee?.full_name || shift.user_name || shift.user_email || `User ${shift.user_id}`,
            "Date": start.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' }),
            "Heure début": start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
            "Heure fin": end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
            "Durée (h)": durationH.replace('.', ','),
            "Absence injustifiée": shift.no_show ? "Oui" : "Non",
            "Publié": shift.is_published ? "Oui" : "Non",
            "Validé": shift.validated ? "Oui" : "Non",
          };
        });

      const wsAll = XLSX.utils.json_to_sheet(allRows.length > 0 ? allRows : [{ "Info": "Aucun shift sur cette période" }]);
      wsAll['!cols'] = colWidthsAll;
      XLSX.utils.book_append_sheet(wb, wsAll, 'Tous les shifts');

      // --- One sheet per selected employee ---
      const usersToExport = sortedUsers.filter(u => exportSelectedUsers.includes(u.id));
      usersToExport.forEach(employee => {
        const empShifts = rangeShifts
          .filter(s => s.user_id === employee.id)
          .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));

        const rows = empShifts.map(shift => {
          const start = new Date(shift.start_time);
          const end = new Date(shift.end_time);
          const durationH = ((end - start) / (1000 * 60 * 60)).toFixed(2);
          return {
            "Date": start.toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' }),
            "Heure début": start.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
            "Heure fin": end.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
            "Durée (h)": durationH.replace('.', ','),
            "Absence injustifiée": shift.no_show ? "Oui" : "Non",
            "Publié": shift.is_published ? "Oui" : "Non",
            "Validé": shift.validated ? "Oui" : "Non",
          };
        });

        const totalHours = empShifts
          .filter(s => !s.no_show)
          .reduce((sum, s) => sum + (new Date(s.end_time) - new Date(s.start_time)) / (1000 * 60 * 60), 0);

        if (rows.length > 0) {
          rows.push({});
          rows.push({ "Date": "TOTAL HEURES", "Heure début": "", "Heure fin": "", "Durée (h)": totalHours.toFixed(2).replace('.', ','), "Absence injustifiée": "", "Publié": "", "Validé": "" });
        }

        const ws = XLSX.utils.json_to_sheet(rows.length > 0 ? rows : [{ "Info": "Aucun shift sur cette période" }]);
        ws['!cols'] = [{ wch: 26 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 22 }, { wch: 10 }, { wch: 10 }];

        const sheetName = (employee.full_name || employee.email || `User${employee.id}`)
          .replace(/[\][*\\/?: ]/g, '')
          .slice(0, 31);

        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });

      const startLabel = exportDateRange.start.replace(/-/g, '');
      const endLabel = exportDateRange.end.replace(/-/g, '');
      const fileName = `Shifts_${startLabel}_${endLabel}.xlsx`;
      XLSX.writeFile(wb, fileName);
      setExportModal({ show: false });
    } catch (err) {
      console.error('Erreur export:', err);
      alert('Erreur lors de la génération du fichier Excel.');
    } finally {
      setExportLoading(false);
    }
  };

  const previousMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  // Get consistent color for user
  const getUserColor = (userId) => {
    const userIndex = users.findIndex(u => u.id === userId);
    return employeeColors[userIndex % employeeColors.length];
  };

  // Sort users according to localStorage order (same logic as GestionShifts)
  const sortedUsers = useMemo(() => {
    return [...users].sort((a, b) => {
      // 1. Sort by is_active (active first)
      const activeA = a.is_active !== false; // handle null/undefined as true
      const activeB = b.is_active !== false;
      if (activeA !== activeB) return activeA ? -1 : 1;

      // 2. Sort according to localStorage order if both share same active status
      if (usersOrder.length > 0) {
        const indexA = usersOrder.indexOf(a.id);
        const indexB = usersOrder.indexOf(b.id);
        if (indexA !== -1 && indexB !== -1) return indexA - indexB;
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
      }

      // 3. Fallback to alphabetical
      return (a.full_name || a.email).localeCompare(b.full_name || b.email);
    });
  }, [users, usersOrder]);

  const LEAVE_TYPE_LABELS = {
    paid_leave: { label: 'CP', short: 'CP', bg: 'bg-emerald-600', text: 'text-white' },
    sick_leave: { label: 'Maladie', short: 'M', bg: 'bg-orange-600', text: 'text-white' },
    work_accident: { label: 'AT', short: 'AT', bg: 'bg-red-600', text: 'text-white' },
    unjustified: { label: 'ABS', short: 'ABS', bg: 'bg-slate-900', text: 'text-white' },
    compensatory: { label: 'RC', short: 'RC', bg: 'bg-purple-600', text: 'text-white' },
  };

  const isOnLeave = (userId, day) => {
    return approvedLeaves.find(leave => {
      if (leave.user_id !== userId || leave.is_day_off) return false;
      const startDate = leave.start_datetime ? leave.start_datetime.slice(0,10) : leave.start_date;
      const endDate   = leave.end_datetime   ? leave.end_datetime.slice(0,10)   : leave.end_date;
      const dayStr = formatDateLocal(day);
      return startDate <= dayStr && endDate >= dayStr;
    });
  };

  const isDayOff = (userId, day) => {
    const dayStr = formatDateLocal(day);
    return dayOffs.some(ev => ev.user_id === userId && ev.date === dayStr);
  };

  // Calculate user statistics
  const getUserStats = useMemo(() => {
    const stats = {};

    sortedUsers.forEach(user => {
      const userShifts = shifts.filter(s => s.user_id === user.id);
      const now = new Date();
      const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1);

      // Calculate hours for current month
      const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
      const currentMonthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);

      const monthShifts = userShifts.filter(s => {
        const shiftDate = new Date(s.start_time);
        return shiftDate >= currentMonthStart && shiftDate <= currentMonthEnd && !s.no_show;
      });

      const totalHours = monthShifts.reduce((sum, shift) => {
        const start = new Date(shift.start_time);
        const end = new Date(shift.end_time);
        return sum + (end - start) / (1000 * 60 * 60);
      }, 0);

      // Count no-shows in last 3 months
      const recentNoShows = shifts.filter(s => {
        const shiftDate = new Date(s.start_time);
        return s.user_id === user.id && s.no_show && shiftDate >= threeMonthsAgo;
      }).length;

      // Count leaves in last 3 months
      const recentLeaves = approvedLeaves.filter(leave => {
        const startField = leave.start_datetime ? leave.start_datetime.slice(0,10) : leave.start_date;
        const leaveStart = new Date(startField);
        return leave.user_id === user.id && leaveStart >= threeMonthsAgo;
      }).length;

      // Calculate expected hours (if weekly_hours is available)
      const expectedMonthlyHours = user.weekly_hours ? (user.weekly_hours * 52) / 12 : null;
      const hoursDifference = expectedMonthlyHours ? totalHours - expectedMonthlyHours : null;

      stats[user.id] = {
        totalHours: totalHours.toFixed(1),
        expectedHours: expectedMonthlyHours ? expectedMonthlyHours.toFixed(1) : null,
        hoursDifference: hoursDifference ? hoursDifference.toFixed(1) : null,
        recentNoShows,
        recentLeaves
      };
    });

    return stats;
  }, [sortedUsers, shifts, approvedLeaves, currentMonth]);

  const getShiftsForDay = (userId, day) => {
    return shifts.filter(shift =>
      shift.user_id === userId &&
      isSameDay(new Date(shift.start_time), day)
    );
  };

  const renderDayCell = (userId, day, userIndex) => {
    const onLeave = isOnLeave(userId, day);
    const dayOff = isDayOff(userId, day);
    const dayShifts = getShiftsForDay(userId, day);
    const isToday = isSameDay(day, new Date());
    const isCurrentMonth = day.getMonth() === currentMonth.getMonth();
    const leaveCfg = onLeave
      ? (LEAVE_TYPE_LABELS[onLeave.leave_type] || { label: 'Congé', bg: 'bg-red-100', text: 'text-red-700' })
      : null;

    return (
      <td
        key={day.toISOString()}
        className={`border border-gray-200 p-1 min-w-[80px] align-top transition-all ${isToday ? 'bg-indigo-50/30' :
            !isCurrentMonth ? 'bg-gray-50/50' : ''
          } ${dayOff ? 'bg-gray-50' : onLeave ? 'bg-orange-50/50' : ''}`}
      >
        {dayOff ? (
          <div className="flex flex-col items-center justify-center gap-1 py-2 text-gray-500" title="Jour de repos">
            <Coffee className="w-4 h-4" />
            <span className="text-xs font-medium">Repos</span>
          </div>
        ) : onLeave && leaveCfg ? (
          <div
            className={`${leaveCfg.bg} ${leaveCfg.text} text-[10px] sm:text-xs px-2 py-1.5 rounded text-center font-bold shadow-sm uppercase tracking-wider ${isManager ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
            title={isManager ? `${onLeave.reason || leaveCfg.label} — cliquer pour supprimer` : (onLeave.reason || leaveCfg.label)}
            onClick={isManager ? () => requestDeleteLeave(onLeave) : undefined}
          >
            {leaveCfg.label}
          </div>
        ) : dayShifts.length > 0 ? (
          <div className="space-y-1">
            {dayShifts.map(shift => {
              const hasPendingRequest = getPendingRequestForShift(shift.id);
              const isNoShow = shift.no_show;
              const primaryTag = shift.tags && shift.tags.length > 0 ? shift.tags[0] : null;
              const additionalTags = shift.tags && shift.tags.length > 1 ? shift.tags.slice(1) : [];

              return (
                <div
                  key={shift.id}
                  className={`relative text-[10px] sm:text-xs px-2 py-1.5 rounded font-medium cursor-pointer transition-all border hover:shadow-md hover:-translate-y-0.5 ${isNoShow ? 'opacity-60' : ''}`}
                  style={isNoShow ? {
                    backgroundColor: '#FEE2E2',
                    borderColor: '#EF4444',
                  } : primaryTag ? {
                    backgroundColor: `${primaryTag.color}18`,
                    borderColor: primaryTag.color,
                  } : {
                    backgroundColor: '#F3F4F6',
                    borderColor: '#D1D5DB',
                  }}
                  onClick={() => handleShiftClick(shift)}
                  title={isNoShow ? 'Absence non justifiée' : `${formatTime(shift.start_time)} - ${formatTime(shift.end_time)}${shift.tags?.length ? ' · ' + shift.tags.map(t => t.name).join(', ') : ''}`}
                >
                  {isNoShow && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 rounded-full flex items-center justify-center" title="Absence non justifiée">
                      <AlertCircle className="w-3 h-3 text-white" />
                    </div>
                  )}
                  {hasPendingRequest && !isNoShow && (
                    <div className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-amber-500 rounded-full border border-white" title="Demande en attente" />
                  )}

                  {/* Time display colored by primary tag */}
                  <div
                    className={`text-center font-semibold ${isNoShow ? 'line-through text-red-600' : ''}`}
                    style={!isNoShow && primaryTag ? { color: primaryTag.color } : {}}
                  >
                    {formatTime(shift.start_time).slice(0, -3)} - {formatTime(shift.end_time).slice(0, -3)}
                  </div>

                  {/* Additional tag color dots (no names) */}
                  {additionalTags.length > 0 && (
                    <div className="flex gap-0.5 mt-1 justify-center">
                      {additionalTags.map((tag) => (
                        <div
                          key={tag.id}
                          className="w-2 h-2 rounded-full border border-white shadow-sm flex-shrink-0"
                          style={{ backgroundColor: tag.color }}
                          title={tag.name}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : null}
      </td>
    );
  };

  const daysInMonth = getDaysInMonth(currentMonth);
  const today = new Date();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 text-gray-800">
      <main className="max-w-full mx-auto px-3 sm:px-6 py-4 sm:py-8">
        {/* Minimalist Header */}
        <div className="bg-white rounded-lg shadow-sm border p-3 sm:px-4 sm:py-3 mb-4 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full lg:w-auto">
            <div className="flex items-center justify-between w-full sm:w-auto">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigate('/gestionShifts')}
                  className="p-1.5 hover:bg-gray-100 text-gray-500 rounded-md transition-colors"
                  title="Retour"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <h1 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-indigo-500" />
                  Vue Mensuelle
                </h1>
              </div>
            </div>

            <div className="hidden sm:block h-5 w-px bg-gray-200"></div>

            {/* Month Navigation */}
            <div className="flex items-center justify-center gap-1 w-full sm:w-auto bg-gray-50/50 rounded-md p-1 border border-gray-100">
              <button
                onClick={previousMonth}
                className="p-1 hover:bg-white hover:shadow-sm rounded text-gray-600 transition-all"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium w-32 text-center text-gray-700 capitalize">
                {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
              </span>
              <button
                onClick={nextMonth}
                className="p-1 hover:bg-white hover:shadow-sm rounded text-gray-600 transition-all"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <div className="w-px h-4 bg-gray-200 mx-1"></div>
              <button
                onClick={goToToday}
                className="px-2 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
              >
                Aujourd'hui
              </button>
            </div>
          </div>

          <button
            onClick={openExportModal}
            className="w-full lg:w-auto flex justify-center items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 active:scale-95 transition-all shadow-sm"
          >
            <FileSpreadsheet className="w-4 h-4 text-emerald-500" />
            <span>Export</span>
          </button>
        </div>

        {/* Tag Color Legend */}
        {tags.length > 0 && (
          <div className="mb-3 p-2 bg-white border border-gray-100 rounded-lg shadow-sm">
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider mr-1">
                Étiquettes :
              </span>
              {tags.map((tag) => (
                <div
                  key={tag.id}
                  className="flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 border border-gray-200"
                >
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: tag.color }}
                  />
                  <span style={{ color: tag.color }}>{tag.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Monthly Calendar */}
        {viewMode === "table" ? (
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="sticky left-0 bg-gray-100 border border-gray-200 p-3 text-left font-semibold min-w-[150px] z-20">
                      Employé
                    </th>
                    {daysInMonth.map((day, idx) => {
                      const isToday = isSameDay(day, today);
                      const dayOfWeek = day.getDay();
                      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                      const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

                      return (
                        <th
                          key={idx}
                          className={`border border-gray-200 p-2 text-center min-w-[80px] ${isToday ? 'bg-indigo-100 font-bold' :
                              !isCurrentMonth ? 'bg-gray-50 opacity-60' :
                                isWeekend ? 'bg-gray-100' : ''
                            }`}
                        >
                          <div className={`text-sm font-semibold ${!isCurrentMonth ? 'text-gray-400' : ''}`}>{day.getDate()}</div>
                          <div className={`text-xs font-normal ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-500'}`}>
                            {dayNamesShort[(dayOfWeek + 6) % 7]}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={daysInMonth.length + 1} className="text-center py-8 text-gray-500">
                        Aucun employé trouvé
                      </td>
                    </tr>
                  ) : (
                    sortedUsers.map((employee, userIndex) => {
                      const stats = getUserStats[employee.id];

                      return (
                        <tr key={employee.id} className="hover:bg-gray-50">
                          <td className={`sticky left-0 bg-white border border-gray-200 p-3 font-medium ${
                            hoveredUserId === employee.id ? 'z-30' : 'z-10'
                          }`}>
                            <div
                              className="flex items-center gap-2 relative group"
                              onMouseEnter={() => setHoveredUserId(employee.id)}
                              onMouseLeave={() => setHoveredUserId(null)}
                            >
                              <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${employeeColors[userIndex % employeeColors.length].bg} flex items-center justify-center text-sm font-semibold ${employeeColors[userIndex % employeeColors.length].text}`}>
                                {(employee.full_name || employee.email).charAt(0).toUpperCase()}
                              </div>
                              <div className={`text-sm ${employee.is_active === false ? 'line-through text-gray-400 italic' : ''}`}>
                                {employee.full_name || employee.email}
                              </div>

                              {/* Tooltip */}
                              {hoveredUserId === employee.id && stats && (
                                <div className="absolute left-full ml-2 top-0 bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 w-64 z-50 pointer-events-none">
                                  <div className="font-semibold mb-2 border-b border-gray-700 pb-2">
                                    Statistiques - {employee.full_name || employee.email}
                                  </div>
                                  <div className="space-y-1.5">
                                    <div className="flex justify-between">
                                      <span className="text-gray-300">Heures ce mois:</span>
                                      <span className="font-medium">{stats.totalHours}h</span>
                                    </div>
                                    {stats.expectedHours && (
                                      <>
                                        <div className="flex justify-between">
                                          <span className="text-gray-300">Heures attendues:</span>
                                          <span className="font-medium">{stats.expectedHours}h</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span className="text-gray-300">Différence:</span>
                                          <span className={`font-medium ${parseFloat(stats.hoursDifference) > 0
                                              ? 'text-green-400'
                                              : parseFloat(stats.hoursDifference) < 0
                                                ? 'text-red-400'
                                                : 'text-gray-300'
                                            }`}>
                                            {parseFloat(stats.hoursDifference) > 0 ? '+' : ''}{stats.hoursDifference}h
                                          </span>
                                        </div>
                                      </>
                                    )}
                                    <div className="border-t border-gray-700 pt-1.5 mt-1.5">
                                      <div className="flex justify-between">
                                        <span className="text-gray-300">Absences (3 mois):</span>
                                        <span className={`font-medium ${stats.recentNoShows > 0 ? 'text-red-400' : 'text-gray-300'
                                          }`}>
                                          {stats.recentNoShows}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-300">Congés (3 mois):</span>
                                        <span className="font-medium text-gray-300">{stats.recentLeaves}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </td>
                          {daysInMonth.map(day => renderDayCell(employee.id, day, userIndex))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          // ── Mobile View: same table but compact + horizontal scroll ──
          <div className="bg-white rounded-lg shadow-lg overflow-hidden">
            <div className="overflow-x-auto -webkit-overflow-scrolling-touch">
              <table className="w-full border-collapse" style={{ minWidth: `${daysInMonth.length * 68 + 130}px` }}>
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="sticky left-0 bg-gray-100 border border-gray-200 p-2 text-left font-semibold min-w-[120px] z-20 text-xs">
                      Employé
                    </th>
                    {daysInMonth.map((day, idx) => {
                      const isToday = isSameDay(day, today);
                      const dayOfWeek = day.getDay();
                      const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
                      const isCurrentMonth = day.getMonth() === currentMonth.getMonth();

                      return (
                        <th
                          key={idx}
                          className={`border border-gray-200 p-1 text-center min-w-[68px] ${isToday ? 'bg-indigo-100 font-bold' :
                              !isCurrentMonth ? 'bg-gray-50 opacity-60' :
                                isWeekend ? 'bg-gray-100' : ''
                            }`}
                        >
                          <div className={`text-xs font-semibold ${!isCurrentMonth ? 'text-gray-400' : ''}`}>{day.getDate()}</div>
                          <div className={`text-[10px] font-normal ${!isCurrentMonth ? 'text-gray-300' : 'text-gray-500'}`}>
                            {dayNamesShort[(dayOfWeek + 6) % 7]}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={daysInMonth.length + 1} className="text-center py-8 text-gray-500 text-sm">
                        Aucun employé trouvé
                      </td>
                    </tr>
                  ) : (
                    sortedUsers.map((employee, userIndex) => {
                      const colors = employeeColors[userIndex % employeeColors.length];
                      const stats = getUserStats[employee.id];

                      return (
                        <tr key={employee.id} className="hover:bg-gray-50">
                          {/* Sticky employee name column */}
                          <td className="sticky left-0 bg-white border border-gray-200 p-2 font-medium z-10 min-w-[120px]">
                            <div className="flex items-center gap-2">
                              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${colors.bg} ${colors.text}`}>
                                {(employee.full_name || employee.email).charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <div className={`text-xs font-semibold truncate ${employee.is_active === false ? 'line-through text-gray-400 italic' : ''}`}>
                                  {(employee.full_name || employee.email).split(' ')[0]}
                                </div>
                                {stats && (
                                  <div className="text-[10px] text-gray-400 truncate">
                                    {stats.totalHours}h
                                    {stats.recentNoShows > 0 && (
                                      <span className="text-red-500 ml-1">· {stats.recentNoShows}abs</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          {daysInMonth.map(day => renderDayCell(employee.id, day, userIndex))}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>

      {/* Shift Action Modal */}
      {shiftActionModal.show && shiftActionModal.shift && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto transform transition-all">
            {/* Header */}
            <div className="flex justify-between items-center p-4 sm:p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
              <h2 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                {shiftActionModal.mode === "edit" ? "Modifier le shift" : shiftActionModal.mode === "view-request" ? "Détails de la demande" : "Demander une modification"}
              </h2>
              <button
                onClick={() => setShiftActionModal({ show: false, shift: null, mode: null })}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1 touch-manipulation"
                aria-label="Fermer"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>

            {/* Shift Details */}
            <div className="p-4 sm:p-6 space-y-4">
              {/* Employee Info */}
              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getUserColor(shiftActionModal.shift.user_id).bg} flex items-center justify-center text-white font-semibold shadow-md`}>
                  {(shiftActionModal.shift.user_name || shiftActionModal.shift.user_email || "?").charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-semibold text-gray-800">
                    {shiftActionModal.shift.user_name || shiftActionModal.shift.user_email || `User ${shiftActionModal.shift.user_id}`}
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(shiftActionModal.shift.start_time).toLocaleDateString("fr-FR", {
                      weekday: "long",
                      day: "numeric",
                      month: "long"
                    })}
                  </div>
                </div>
              </div>

              {/* Manager Edit Mode */}
              {shiftActionModal.mode === "edit" && (() => {
                const pendingRequest = getPendingRequestForShift(shiftActionModal.shift.id);

                return (
                  <>
                    {/* Pending Request Alert */}
                    {pendingRequest && (
                      <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4 mb-4">
                        <div className="flex items-center gap-2 mb-2">
                          <AlertCircle className="w-5 h-5 text-orange-600" />
                          <h3 className="font-semibold text-orange-800">Demande de modification en attente</h3>
                        </div>
                        <div className="text-sm text-gray-700">
                          <p>Nouvelles heures proposées :</p>
                          <p className="font-medium mt-1">
                            {pendingRequest.start_time?.slice(11, 16) || '??:??'} - {pendingRequest.end_time?.slice(11, 16) || '??:??'}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            Demandé le {new Date(pendingRequest.created_at).toLocaleDateString("fr-FR")}
                          </p>
                        </div>
                      </div>
                    )}

                    {!pendingRequest && (
                      <div className="space-y-4">
                        {/* User Transfer Selector (Manager only) */}
                        {isTeamLeaderOrAbove && (
                          <div>
                            <label htmlFor="edit-user-select" className="block text-sm font-medium text-gray-700 mb-2">
                              Employé
                            </label>
                            <select
                              id="edit-user-select"
                              name="user_id"
                              value={editShift.user_id}
                              onChange={(e) => setEditShift({ ...editShift, user_id: e.target.value })}
                              className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base"
                            >
                              {sortedUsers.map((u) => (
                                <option key={u.id} value={u.id}>
                                  {u.full_name || u.email}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        <div className="grid grid-cols-2 gap-3 sm:gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Début du shift
                            </label>
                            <input
                              type="datetime-local"
                              value={editShift.start_datetime}
                              onChange={(e) => setEditShift({ ...editShift, start_datetime: e.target.value })}
                              className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm sm:text-base min-w-0 max-w-full box-border"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Fin du shift
                            </label>
                            <input
                              type="datetime-local"
                              value={editShift.end_datetime}
                              onChange={(e) => setEditShift({ ...editShift, end_datetime: e.target.value })}
                              className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-sm sm:text-base min-w-0 max-w-full box-border"
                            />
                          </div>
                        </div>

                        {/* Pause (Break Duration) */}
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 mt-4">
                          <label className="block text-sm font-medium text-amber-800 mb-2 flex items-center gap-2">
                            <Coffee className="w-4 h-4" />
                            Temps de pause (minutes)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="5"
                            value={editShift.break_duration}
                            onChange={(e) => setEditShift({ ...editShift, break_duration: parseInt(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-400 focus:border-transparent transition-all text-base bg-white"
                          />
                          {editShift.start_datetime && editShift.end_datetime && (() => {
                            const totalMin = (new Date(editShift.end_datetime) - new Date(editShift.start_datetime)) / (1000 * 60);
                            const netMin = Math.max(0, totalMin - (parseInt(editShift.break_duration) || 0));
                            const h = Math.floor(netMin / 60);
                            const m = netMin % 60;
                            return (
                              <p className="text-xs text-amber-700 mt-1.5">
                                Total brut : {Math.floor(totalMin / 60)}h{totalMin % 60 > 0 ? `${totalMin % 60}m` : ''}
                                {' → '}Net : <strong>{h}h{m > 0 ? `${m}m` : ''}</strong>
                              </p>
                            );
                          })()}
                        </div>

                        {/* Commentaire */}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-1.5 flex items-center gap-2">
                            <AlertCircle className="w-4 h-4" />
                            Commentaire (optionnel)
                          </label>
                          <textarea
                            value={editShift.comment || ""}
                            onChange={(e) => setEditShift({ ...editShift, comment: e.target.value })}
                            rows="2"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base bg-white resize-none"
                            placeholder="Note pour ce shift..."
                          ></textarea>
                        </div>

                        {/* Salle */}
                        <div className="mt-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Salle (optionnel)
                          </label>
                          <select
                            value={editShift.room_id}
                            onChange={(e) => setEditShift({ ...editShift, room_id: e.target.value })}
                            className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base"
                          >
                            <option value="">Aucune salle</option>
                            {rooms.map((room) => (
                              <option key={room.id} value={room.id}>
                                {room.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Tags */}
                        {tags.length > 0 && (
                          <div className="mt-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Étiquettes (optionnel)
                            </label>
                            <div className="flex flex-wrap gap-2">
                              {tags.map((tag) => (
                                <button
                                  key={tag.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedTagIds(prev =>
                                      prev.includes(tag.id)
                                        ? prev.filter(id => id !== tag.id)
                                        : [...prev, tag.id]
                                    );
                                  }}
                                  className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${selectedTagIds.includes(tag.id)
                                    ? 'ring-2 ring-offset-2 ring-indigo-500'
                                    : 'hover:opacity-80'
                                    }`}
                                  style={{
                                    backgroundColor: tag.color,
                                    color: '#ffffff',
                                  }}
                                >
                                  {selectedTagIds.includes(tag.id) && <Check className="w-3 h-3 inline mr-1" />}
                                  {tag.name}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* No-show Toggle */}
                        <div className="mt-4 flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100">
                          <div className="flex items-center gap-2 text-red-700">
                            <AlertCircle className="w-4 h-4" />
                            <span className="text-sm font-medium">Absence non justifiée</span>
                          </div>
                          <button
                            type="button"
                            role="switch"
                            aria-checked={editShift.no_show}
                            onClick={() => setEditShift({ ...editShift, no_show: !editShift.no_show })}
                            className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 touch-manipulation ${editShift.no_show ? 'bg-red-600' : 'bg-gray-200'}`}
                          >
                            <span className="sr-only">Toggle absence</span>
                            <span
                              className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${editShift.no_show ? 'translate-x-5' : 'translate-x-0'}`}
                            />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex gap-3 pt-4">
                      {pendingRequest ? (
                        <>
                          <button
                            onClick={handleRejectRequest}
                            disabled={loading}
                            className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg bg-red-100 hover:bg-red-200 text-red-700 transition-colors touch-manipulation disabled:opacity-50"
                          >
                            <X className="w-4 h-4" />
                            Refuser
                          </button>
                          <button
                            onClick={handleModifyFromRequest}
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                          >
                            <Edit2 className="w-4 h-4" />
                            {loading ? "..." : "Modifier"}
                          </button>
                          <button
                            onClick={handleApproveRequest}
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white hover:shadow-lg active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation"
                          >
                            <Check className="w-4 h-4" />
                            {loading ? "..." : "Valider"}
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={handleDeleteShift}
                            disabled={loading}
                            className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg bg-red-100 hover:bg-red-200 text-red-700 transition-colors touch-manipulation disabled:opacity-50"
                          >
                            <Trash2 className="w-4 h-4" />
                            Supprimer
                          </button>
                          <button
                            onClick={handleUpdateShift}
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none touch-manipulation"
                          >
                            <Edit2 className="w-4 h-4" />
                            {loading ? "Modification..." : "Modifier"}
                          </button>
                        </>
                      )}
                    </div>
                  </>
                );
              })()}

              {/* User Request Mode */}
              {shiftActionModal.mode === "request" && (
                <>
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Détails du shift actuel</h3>
                    
                    <div className="space-y-3">
                      <div>
                        <span className="block text-xs text-gray-500">Horaires</span>
                        <div className="font-medium text-gray-800">
                          {shiftActionModal.shift.start_time?.slice(11, 16)} - {shiftActionModal.shift.end_time?.slice(11, 16)}
                        </div>
                      </div>
                      
                      {shiftActionModal.shift.room_name && (
                        <div>
                          <span className="block text-xs text-gray-500">Salle</span>
                          <div className="font-medium text-gray-800">{shiftActionModal.shift.room_name}</div>
                        </div>
                      )}

                      {shiftActionModal.shift.break_duration > 0 && (
                        <div>
                          <span className="block text-xs text-gray-500">Pause</span>
                          <div className="font-medium text-gray-800">{shiftActionModal.shift.break_duration} minutes</div>
                        </div>
                      )}

                      {shiftActionModal.shift.comment && (
                        <div>
                          <span className="block text-xs text-gray-500">Commentaire</span>
                          <div className="text-sm text-gray-700">{shiftActionModal.shift.comment}</div>
                        </div>
                      )}

                      {/* Display existing tags */}
                      {shiftActionModal.shift.tags && shiftActionModal.shift.tags.length > 0 && (
                        <div>
                          <span className="block text-xs text-gray-500 mb-1">Étiquettes</span>
                          <div className="flex flex-wrap gap-2">
                            {shiftActionModal.shift.tags.map((tag) => (
                              <span
                                key={tag.id}
                                className="px-2 py-1 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: tag.color }}
                              >
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <hr className="my-4 border-gray-200" />
                  <div className="grid grid-cols-2 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nouvelle heure de début
                      </label>
                      <input
                        type="time"
                        value={requestModification.start_time}
                        onChange={(e) => setRequestModification({ ...requestModification, start_time: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nouvelle heure de fin
                      </label>
                      <input
                        type="time"
                        value={requestModification.end_time}
                        onChange={(e) => setRequestModification({ ...requestModification, end_time: e.target.value })}
                        className="w-full px-3 sm:px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all text-base"
                      />
                    </div>
                  </div>

                  {/* Action Button for User */}
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setShiftActionModal({ show: false, shift: null, mode: null })}
                      className="flex-1 px-4 py-3 text-sm font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors touch-manipulation"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleRequestModification}
                      disabled={loading}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:shadow-lg active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none touch-manipulation"
                    >
                      <Send className="w-4 h-4" />
                      {loading ? "Envoi..." : "Envoyer la demande"}
                    </button>
                  </div>
                </>
              )}

              {/* View Request Mode (Non-Manager viewing their own request) */}
              {shiftActionModal.mode === "view-request" && shiftActionModal.request && (
                <>
                  {/* Request Status Badge */}
                  <div className="flex items-center justify-center mb-4">
                    <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${shiftActionModal.request.status === 'pending'
                        ? 'bg-orange-100 text-orange-700 border-2 border-orange-200'
                        : shiftActionModal.request.status === 'approved'
                          ? 'bg-green-100 text-green-700 border-2 border-green-200'
                          : 'bg-red-100 text-red-700 border-2 border-red-200'
                      }`}>
                      <AlertCircle className="w-4 h-4" />
                      {shiftActionModal.request.status === 'pending' && 'En attente'}
                      {shiftActionModal.request.status === 'approved' && 'Approuvée'}
                      {shiftActionModal.request.status === 'rejected' && 'Rejetée'}
                    </span>
                  </div>

                  {/* Current Times */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Détails du shift actuel</h3>
                    <div className="flex items-center justify-center gap-2 text-lg font-medium text-gray-800 mb-3">
                      <span>{shiftActionModal.shift.start_time?.slice(11, 16) || '??:??'}</span>
                      <span className="text-gray-400">→</span>
                      <span>{shiftActionModal.shift.end_time?.slice(11, 16) || '??:??'}</span>
                    </div>

                    <div className="space-y-3 pt-3 border-t border-gray-200 mt-3 text-left">
                      {shiftActionModal.shift.room_name && (
                        <div>
                          <span className="block text-xs text-gray-500">Salle</span>
                          <div className="font-medium text-gray-800">{shiftActionModal.shift.room_name}</div>
                        </div>
                      )}

                      {shiftActionModal.shift.break_duration > 0 && (
                        <div>
                          <span className="block text-xs text-gray-500">Pause</span>
                          <div className="font-medium text-gray-800">{shiftActionModal.shift.break_duration} minutes</div>
                        </div>
                      )}

                      {shiftActionModal.shift.comment && (
                        <div>
                          <span className="block text-xs text-gray-500">Commentaire</span>
                          <div className="text-sm text-gray-700">{shiftActionModal.shift.comment}</div>
                        </div>
                      )}

                      {shiftActionModal.shift.tags && shiftActionModal.shift.tags.length > 0 && (
                        <div>
                          <span className="block text-xs text-gray-500 mb-1">Étiquettes</span>
                          <div className="flex flex-wrap gap-2">
                            {shiftActionModal.shift.tags.map((tag) => (
                              <span
                                key={tag.id}
                                className="px-2 py-1 rounded-full text-xs font-medium text-white"
                                style={{ backgroundColor: tag.color }}
                              >
                                {tag.name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Requested Times */}
                  <div className="bg-indigo-50 rounded-lg p-4 mb-4 border-2 border-indigo-200">
                    <h3 className="text-sm font-semibold text-indigo-700 mb-3">Horaires demandés</h3>
                    <div className="flex items-center justify-center gap-2 text-lg font-medium text-indigo-800">
                      <span>{shiftActionModal.request.start_time?.slice(11, 16) || '??:??'}</span>
                      <span className="text-indigo-400">→</span>
                      <span>{shiftActionModal.request.end_time?.slice(11, 16) || '??:??'}</span>
                    </div>
                  </div>

                  {/* Reason (if exists) */}
                  {shiftActionModal.request.reason && (
                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-2">Raison</h3>
                      <p className="text-sm text-gray-600">{shiftActionModal.request.reason}</p>
                    </div>
                  )}

                  {/* Request Date */}
                  <div className="text-xs text-gray-500 text-center mb-4">
                    Demandé le {new Date(shiftActionModal.request.created_at).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </div>

                  {/* Close Button */}
                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => setShiftActionModal({ show: false, shift: null, mode: null, request: null })}
                      className="flex-1 px-4 py-3 text-sm font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors touch-manipulation"
                    >
                      Fermer
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Export Modal ── */}
      {exportModal.show && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-5 border-b border-gray-200">
              <h2 className="text-lg font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                Export des Shifts
              </h2>
              <button
                onClick={() => setExportModal({ show: false })}
                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {/* Date range */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-3">Période</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Date de début</label>
                    <input
                      type="date"
                      value={exportDateRange.start}
                      onChange={e => setExportDateRange(prev => ({ ...prev, start: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Date de fin</label>
                    <input
                      type="date"
                      value={exportDateRange.end}
                      onChange={e => setExportDateRange(prev => ({ ...prev, end: e.target.value }))}
                      min={exportDateRange.start}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* User selection */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-semibold text-gray-700">Employés</p>
                  <div className="flex gap-2 text-xs">
                    <button
                      onClick={() => setExportSelectedUsers(sortedUsers.map(u => u.id))}
                      className="text-emerald-600 hover:text-emerald-700 font-medium"
                    >
                      Tout sélectionner
                    </button>
                    <span className="text-gray-300">|</span>
                    <button
                      onClick={() => setExportSelectedUsers([])}
                      className="text-gray-500 hover:text-gray-700 font-medium"
                    >
                      Tout désélectionner
                    </button>
                  </div>
                </div>

                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                  {sortedUsers.length === 0 ? (
                    <p className="text-sm text-gray-400 text-center py-4">Aucun employé disponible</p>
                  ) : (
                    sortedUsers.map((employee, idx) => {
                      const colors = employeeColors[idx % employeeColors.length];
                      const checked = exportSelectedUsers.includes(employee.id);
                      return (
                        <label
                          key={employee.id}
                          className={`flex items-center gap-3 p-2.5 rounded-lg cursor-pointer transition-colors ${checked ? `${colors.bg} border border-opacity-50 ${colors.border}` : 'hover:bg-gray-50 border border-transparent'
                            }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleExportUser(employee.id)}
                            className="w-4 h-4 accent-emerald-600 flex-shrink-0"
                          />
                          <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${colors.bg} ${colors.text}`}>
                            {(employee.full_name || employee.email).charAt(0).toUpperCase()}
                          </div>
                          <span className={`text-sm font-medium ${checked ? colors.text : 'text-gray-700'} ${employee.is_active === false ? 'line-through opacity-50 italic' : ''}`}>
                            {employee.full_name || employee.email}
                          </span>
                        </label>
                      );
                    })
                  )}
                </div>

                <p className="text-xs text-gray-400 mt-2">
                  {exportSelectedUsers.length} / {sortedUsers.length} employé{sortedUsers.length > 1 ? 's' : ''} sélectionné{exportSelectedUsers.length > 1 ? 's' : ''}
                </p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={() => setExportModal({ show: false })}
                  className="flex-1 px-4 py-3 text-sm font-medium rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleExportShifts}
                  disabled={exportLoading || exportSelectedUsers.length === 0 || !exportDateRange.start || !exportDateRange.end}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-lg bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:shadow-lg active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  {exportLoading ? 'Génération...' : 'Télécharger (.xlsx)'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!leaveToDelete}
        variant="danger"
        title="Supprimer ce congé ?"
        message="Le solde de congés sera restitué le cas échéant. Cette action est irréversible."
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        loading={deletingLeave}
        onConfirm={confirmDeleteLeave}
        onCancel={() => !deletingLeave && setLeaveToDelete(null)}
      />
    </div>
  );
}
